import {
  Component,
  computed,
  Inject,
  OnDestroy,
  signal,
  Signal,
  ViewChild,
} from '@angular/core';
import { AbbModalComponent } from '@components/abb-modal/abb-modal.component';
import { MODAL_DATA, ModalData } from '@components/abb-modal/modal';
import { TPipe, TRANSLATION_PREFIX } from '@pipes/t.pipe';
import { AbbButtonComponent } from '@components/abb-button/abb-button.component';
import { BehaviorSubject, Subscription } from 'rxjs';
import { Apollo } from 'apollo-angular';
import { CognitoService, UserWithID } from '@services/cognito/cognito.service';
import { getErrorMessage } from '@utils/errors';
import { ToastService } from '@services/ui/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { ShadowScrollbarContainerComponent } from '@components/abb-shadow-scrollbar-container/abb-shadow-scrollbar-container.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { SelectionModel } from '@angular/cdk/collections';
import {
  RequestFirmwareUpdateMutation,
  RequestFirmwareUpdateMutationVariables,
  SystemSettingsQuery,
} from 'gql/graphql';
import { OverflowTooltipDirective } from '@directives/overflow-tooltip.directive';
import { requestFirmwareUpdateMutation } from '@graphql/commissioning';
import { MatRadioModule } from '@angular/material/radio';

export type SystemSettings = Omit<
  SystemSettingsQuery['systemSettings'],
  'scu200List'
> & {
  scu200List: (Omit<
    NonNullable<SystemSettingsQuery['systemSettings']['scu200List'][number]>,
    'firmware'
  > &
    Pick<
      NonNullable<SystemSettingsQuery['systemSettings']['scu200List'][number]>,
      'firmware'
    > & {
      firmware: {
        updating?: boolean;
      };
    })[];
};
export type SystemSettingsModalData = {
  systemSettings: BehaviorSubject<SystemSettings | null>;
  loading?: boolean;
};

@Component({
  selector: 'abb-system-settings-modal',
  standalone: true,
  template: `
    <abb-modal [title]="'SCU200 firmware update' | t" #modalRef>
      <div class="flex flex-col items-center gap-2">
        <div class="w-full flex flex-col !bg-abb-black/[0.02]">
          <abb-shadow-scrollbar-container class="!h-40">
            @if (!!data.loading) {
            <div class="block text-center content-center h-full w-full">
              <mat-spinner class="mx-auto" [diameter]="50"></mat-spinner>
            </div>
            } @else if (noData()) {
            <span class="block h-full text-center content-center">{{
              'No data' | t
            }}</span>
            } @else {
            <!-- prettier-ignore -->
            @if ((data.systemSettings | async)?.scu200List; as SCU200List) { @if(updatingFw()){
            <div class="inset-0 absolute flex items-center justify-center">
              <mat-progress-spinner
                [diameter]="40"
                color="primary"
                mode="indeterminate"
              ></mat-progress-spinner>
            </div>
            }
            <div
              class="grid grid-cols-[10%_20%_20%_20%_1fr] gap-x-2 items-center min-w-[40vw]"
              [ngClass]="{
                'opacity-50 pointer-events-none': updatingFw()
              }"
            >
              @for (scu200 of SCU200List; track scu200.id) {
              <mat-radio-button
                [value]="scu200.id"
                [checked]="selectionSCU200.isSelected(scu200)"
                (change)="selectionSCU200.select(scu200)"
                [disabled]="!scu200?.firmware?.updateAvailable"
                color="primary"
                class="border-font-checkbox no-hover-checkbox"
              ></mat-radio-button>
              <div overflowTooltip class="block truncate">
                {{ scu200?.name }}
              </div>
              <div overflowTooltip class="block truncate">
                {{ scu200?.property?.name }}
              </div>
              <div overflowTooltip class="block truncate">
                FW {{ scu200?.firmware?.version }}
              </div>

              @if (scu200?.firmware?.updating) {
              <span class="block truncate">{{ 'Updating' | t }}...</span>
              } @else if (scu200?.firmware?.updateAvailable) {
              <a
                overflowTooltip
                class="block truncate underline"
                [ngClass]="
                  selectionSCU200.selected.length !== 1 ||
                  selectionSCU200.selected[0]?.id !== scu200?.id
                    ? 'text-muted-600 hover:cursor-not-allowed pointer-events-none'
                    : 'text-abb-primary/80 hover:text-abb-primary transition-colors cursor-pointer'
                "
                (click)="updateFw($event, [scu200])"
              >
                {{ 'Update available' | t }}
              </a>
              } @else {
              <span class="block truncate">{{ 'Updated' | t }}</span>
              } }
            </div>
            }}
          </abb-shadow-scrollbar-container>
        </div>
      </div>
      <div footer class="w-full flex flex-row gap-8 justify-center">
        <abb-button
          [loading]="!btnDisabled() && updatingFw()"
          [disabled]="(!btnDisabled() && updatingFw()) || btnDisabled()"
          class="self-end"
          (onclick)="updateFw($event, selectionSCU200.selected)"
          >{{ 'Update firmware' | t }}</abb-button
        >
      </div>
    </abb-modal>
  `,
  styles: ``,
  imports: [
    AbbModalComponent,
    TPipe,
    AbbButtonComponent,
    CommonModule,
    ShadowScrollbarContainerComponent,
    MatProgressSpinnerModule,
    OverflowTooltipDirective,
    MatRadioModule,
  ],
  providers: [
    { provide: TRANSLATION_PREFIX, useValue: 'settings.tabs.general' },
  ],
})
export class SystemSettingsModalComponent
  implements ModalData<SystemSettingsModalData>, OnDestroy
{
  @ViewChild('modalRef') modalRef: AbbModalComponent | undefined;

  user: Signal<UserWithID | null>;
  selectionSCU200 = new SelectionModel<
    SystemSettingsQuery['systemSettings']['scu200List'][number]
  >(false, []);
  selectedSCU200Id = computed(() =>
    this.selectionSCU200.selected.map((scu) => scu?.id)
  );

  noData = computed(
    () => this.data.systemSettings.value?.scu200List?.length === 0
  );
  btnDisabled = signal(true);

  private subscriptions: Subscription[] = [];
  constructor(
    @Inject(MODAL_DATA) public data: SystemSettingsModalData,
    private readonly cognitoService: CognitoService,
    private readonly apollo: Apollo,
    private readonly toast: ToastService,
    private readonly translate: TranslateService
  ) {
    this.user = cognitoService.loggedUser;
    this.subscriptions.push(
      this.selectionSCU200.changed.subscribe((s) => {
        const selected = s.source.selected[0];
        const enabled = !!selected?.firmware?.updateAvailable;
        this.btnDisabled.set(!enabled);
      })
    );
    
  }

  updatingFw = signal(false);
  updateFw(
    event: Event,
    scu200: SystemSettingsQuery['systemSettings']['scu200List']
  ): void {
    this.updatingFw.set(true);
    const scu200Ids = scu200.map((s) => s?.id);
    this.setUpdatingRows(scu200Ids, true);
    this.subscriptions.push(
      this.apollo
        .mutate<
          RequestFirmwareUpdateMutation,
          RequestFirmwareUpdateMutationVariables
        >({
          mutation: requestFirmwareUpdateMutation,
          variables: {
            devices: scu200.map((s) => ({
              id: s!.id,
              serialNumberSCU200: s!.serialNumber ?? undefined,
            })),
          },
        })
        .subscribe({
          next: ({ data }) => {
            this.updatingFw.set(false);
            this.setUpdatingRows(scu200Ids, false);
            this.selectionSCU200.clear();
            const current = this.data.systemSettings.value;
            if (current) {
              current.scu200List.forEach((scu) => {
                if (scu200Ids.includes(scu.id)) {
                  scu.firmware.updateAvailable = false;
                }
              });
              this.data.systemSettings.next({ ...current });
            }
          
            // this.apollo.client.refetchQueries({
            //   include: ['SystemSettings'],
            // });
          },          
          error: (err) => {
            this.updatingFw.set(false);
            this.setUpdatingRows(scu200Ids, false);
            this.selectionSCU200.clear();
            console.error(err);
          },
        })
    );
  }

  private setUpdatingRows(rowsId: (string | undefined)[], value: boolean) {
    this.data.systemSettings.value?.scu200List
      .filter((s) => rowsId.includes(s.id))
      ?.forEach((s) => (s.firmware.updating = value));
  }

  onCancel() {
    this.modalRef?.dispose();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
}
